/**
 * Multi-inbox selection for the campaign launch wizard (rotation_email.md).
 *
 * A campaign may send from several verified inboxes; contacts are spread
 * across them and each contact keeps one inbox for its whole sequence. The
 * list is ordered: the first pick is the campaign's primary sender, which is
 * what every pre-rotation reader of `sender_email_id` still sees.
 *
 * Pure so the rules can be tested without the modal.
 */
import type { SenderEmail } from '@/types/senderEmail'

const addressOf = (s: SenderEmail) => (s.email_address || '').trim().toLowerCase()

/**
 * The already-selected sender that is the SAME MAILBOX as `sender` (same
 * address under a different row), or null. A mailbox has one daily limit
 * however many sender rows point at it, so it may be picked once per
 * campaign; the backend refuses it too, this just says so before submit.
 */
export function sameMailboxConflict(
  sender: SenderEmail,
  selectedIds: number[],
  all: SenderEmail[],
): SenderEmail | null {
  const address = addressOf(sender)
  if (!address) return null
  for (const id of selectedIds) {
    if (id === sender.id) continue
    const other = all.find(s => s.id === id)
    if (other && addressOf(other) === address) return other
  }
  return null
}

/** Add or remove `sender` from the ordered selection. Refuses to add a row
 *  that is the same mailbox as one already selected. */
export function toggleSenderSelection(
  selectedIds: number[],
  sender: SenderEmail,
  all: SenderEmail[],
): number[] {
  if (selectedIds.includes(sender.id)) return selectedIds.filter(id => id !== sender.id)
  if (sameMailboxConflict(sender, selectedIds, all)) return selectedIds
  return [...selectedIds, sender.id]
}

/** The payload fields for the create call. One inbox is sent exactly as it
 *  always was; several send both, so an older backend still gets a valid
 *  single sender (the first pick). */
export function senderPayload(selectedIds: number[]): { sender_email_id?: number; sender_email_ids?: number[] } {
  if (selectedIds.length === 0) return {}
  if (selectedIds.length === 1) return { sender_email_id: selectedIds[0] }
  return { sender_email_id: selectedIds[0], sender_email_ids: [...selectedIds] }
}

/** One line for the review step. */
export function describeSenderSelection(selectedIds: number[], all: SenderEmail[]): string {
  const picked = selectedIds.map(id => all.find(s => s.id === id)).filter((s): s is SenderEmail => Boolean(s))
  if (picked.length === 0) return '—'
  if (picked.length === 1) return `${picked[0].label} (${picked[0].email_address})`
  return `${picked.length} inboxes: ${picked.map(s => s.label).join(', ')}`
}
